import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper } from "antd-mobile";
import {
  getTrainingScenes,
  type TrainingSceneInfo,
  TrainingStatus,
} from "@/lib/api/ai-training/scene";
import { Toast } from "antd-mobile";
import { motion } from "framer-motion";

// 使用API中定义的接口
type TaskItem = TrainingSceneInfo;

interface UrgentTaskCardProps {
  tasks?: TaskItem[];
  className?: string;
  style?: React.CSSProperties;
}

const UrgentTaskCard: React.FC<UrgentTaskCardProps> = ({
  tasks: propTasks,
  className = "",
  style = {},
}) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // 获取紧急任务数据
  const fetchUrgentTasks = async () => {
    if (propTasks && propTasks.length > 0) {
      setTasks(propTasks);
      return;
    }

    setLoading(true);
    try {
      const response = await getTrainingScenes({
        time_status: TrainingStatus.IN_PROGRESS,
        page: 1,
        pageSize: 3, // 只获取前3个作为紧急任务
      });

      if (response.success && response.data) {
        setTasks(response.data.list);
      }
    } catch (error) {
      console.error("获取紧急任务失败:", error);
      Toast.show({
        content: "获取紧急任务失败",
        icon: "fail",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartTask = (task: TaskItem) => {
    console.log("开始任务:", task.scenario_id);
    navigate(`/ai-training/detail/${task.scenario_id}`);
  };

  const handleViewMore = () => {
    console.log("查看更多任务");
    navigate("/ai-training/list");
  };

  useEffect(() => {
    fetchUrgentTasks();
  }, [propTasks]);

  return (
    <motion.div
      style={style}
      className={`max-w-[720px] m-auto h-[80vh] bg-gradient-to-b from-white/60 to-white/100 border-t-2 border-white rounded-3xl ${className}`}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{
        type: "spring",
        stiffness: 140,
        damping: 20,
        duration: 0.2,
      }}
    >
      {/* 标题 */}
      <motion.div
        className='p-4'
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        <span className='text-xl font-bold bg-gradient-to-r from-[#A782FF] to-[#828EFF] bg-clip-text text-transparent'>紧急任务</span>
        <motion.div
          className='w-8 h-1 ml-6 mt-1 bg-[#A782FF] rounded-full'
          initial={{ width: 0 }}
          animate={{ width: 32 }}
          transition={{ delay: 0.4, duration: 0.2 }}
        ></motion.div>
      </motion.div>

      {/* 轮播图 */}
      <motion.div
        className='mb-6'
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        {loading ? (
          <div className='flex justify-center items-center h-[200px]'>
            <div className='text-gray-500 text-sm'>加载中...</div>
          </div>
        ) : tasks.length === 0 ? (
          <div className='flex justify-center items-center h-[200px]'>
            <div className='text-gray-500 text-sm'>暂无紧急任务</div>
          </div>
        ) : (
          <>
            <style
              dangerouslySetInnerHTML={{
                __html: `
               .adm-swiper-slide {
                  transform: scale(0.9) rotate(-10deg) translateY(10px);
                  transition: all 0.3s ease;
                }
                .adm-swiper-slide-active {
                  transform: scale(1);
                }
                .adm-swiper-slide-active ~ .adm-swiper-slide {
                  transform: scale(0.9) rotate(10deg) translateY(10px);
                }
              `,
              }}
            />
            <Swiper
              autoplay
              autoplayInterval={3000}
              slideSize={80}
              trackOffset={10}
              stuckAtBoundary={false}
              indicatorProps={{
                style: {
                  "--dot-color": "rgba(0, 0, 0, 0.3)",
                  "--active-dot-color": "#8b5cf6",
                  "--dot-size": "8px",
                  "--active-dot-size": "8px",
                  "--dot-spacing": "4px",
                },
              }}
            >
              {tasks.map((task) => (
                <Swiper.Item key={task.id} className={`custom-${task.id} py-4 px-2`}>
                  <motion.div
                    className='w-full h-full shadow-lg bg-white rounded-2xl p-3 mx-1'
                    whileHover={{
                      scale: 1.02,
                      boxShadow:
                        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  >
                    {/* 任务图片区域 */}
                    <div className='mb-3 h-[150px] flex items-center justify-center rounded-md overflow-hidden'>
                      <img
                        src={
                          task.scenario_cover ||
                          "https://gips1.baidu.com/it/u=3097268455,1488248219&fm=3074&app=3074&f=PNG?w=2048&h=2048"
                        }
                        alt={task.scenario_name}
                        className='w-full h-full object-cover'
                      />
                    </div>

                    {/* 任务信息 */}
                    <div className='space-y-2'>
                      <h3 className='text-gray-800 font-medium text-base leading-relaxed'>
                        {task.scenario_name}
                      </h3>
                      <p className='text-gray-500 text-sm'>
                        {task.effective_start_time && task.effective_end_time
                          ? `${task.effective_start_time.split("T")[0]}～${
                              task.effective_end_time.split("T")[0]
                            }`
                          : "长期有效"}
                      </p>

                      {/* 开始按钮 */}
                      <motion.button
                        onClick={() => handleStartTask(task)}
                        className='bg-gradient-to-r from-[#4E8FFD] to-[#A785F5] text-white px-6 py-2 rounded-full font-medium text-sm shadow-lg'
                        whileHover={{
                          scale: 1.05,
                          boxShadow: "0 10px 20px rgba(139, 92, 246, 0.3)",
                          background:
                            "linear-gradient(to right, #7c3aed, #6d28d9)",
                        }}
                        whileTap={{ scale: 0.95 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 17,
                        }}
                      >
                        立即开始
                      </motion.button>
                    </div>
                  </motion.div>
                </Swiper.Item>
              ))}
            </Swiper>
          </>
        )}
      </motion.div>

      {/* 查看更多按钮 */}
      <motion.div
        className='text-center'
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.2 }}
      >
        <motion.button
          onClick={handleViewMore}
          className='text-purple-600 border border-purple-600 px-6 py-2 mt-8 rounded-full font-medium text-sm hover:bg-purple-50 transition-all duration-200'
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          查看更多
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default UrgentTaskCard;
